import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

import KPIWidget from './components/KPIWidget';
import PerformanceChart from './components/PerformanceChart';
import TechnicianPerformanceTable from './components/TechnicianPerformanceTable';
import SystemHealthMonitor from './components/SystemHealthMonitor';
import AnalyticsFilters from './components/AnalyticsFilters';

const PerformanceAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dateRange, setDateRange] = useState('month');
  const [department, setDepartment] = useState('all');
  const navigate = useNavigate();

  // Mock data for KPIs
  const kpiData = [
    {
      title: 'Produtividade Média',
      value: '87%',
      change: '+5%',
      changeType: 'positive',
      icon: 'TrendingUp',
      description: 'Taxa de conclusão de tarefas',
      color: 'primary'
    },
    {
      title: 'Taxa de Conclusão',
      value: '92%',
      change: '+8%',
      changeType: 'positive',
      icon: 'CheckCircle',
      description: 'Projetos entregues no prazo',
      color: 'success'
    },
    {
      title: 'Satisfação do Cliente',
      value: '4.8/5',
      change: '+0.3',
      changeType: 'positive',
      icon: 'Star',
      description: 'Avaliação média dos clientes',
      color: 'warning'
    },
    {
      title: 'Receita Mensal',
      value: 'R$ 245.8K',
      change: '+12%',
      changeType: 'positive',
      icon: 'DollarSign',
      description: 'Faturamento do período',
      color: 'accent'
    },
    {
      title: 'Tempo Médio Resposta',
      value: '2.4h',
      change: '-15%',
      changeType: 'positive',
      icon: 'Clock',
      description: 'Tempo de primeira resposta',
      color: 'primary'
    },
    {
      title: 'SLA Compliance',
      value: '94%',
      change: '+2%',
      changeType: 'positive',
      icon: 'Target',
      description: 'Cumprimento de prazos',
      color: 'success'
    }
  ];

  // Mock data for charts
  const projectTimelineData = {
    labels: ['Nov/24', 'Dez/24', 'Jan/25', 'Fev/25', 'Mar/25', 'Abr/25'],
    datasets: [
      {
        label: 'Projetos Iniciados',
        data: [12, 19, 15, 22, 18, 25],
        color: '#3b82f6'
      },
      {
        label: 'Projetos Concluídos',
        data: [8, 15, 12, 18, 16, 21],
        color: '#10b981'
      }
    ]
  };

  const technicianUtilizationData = {
    labels: ['Carlos', 'Pedro', 'Ana', 'Ricardo', 'Maria', 'João'],
    datasets: [
      {
        label: 'Utilização (%)',
        data: [92, 88, 95, 78, 85, 90],
        color: '#8b5cf6'
      }
    ]
  };

  const demandPatternsData = {
    labels: ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'],
    datasets: [
      {
        label: 'Demandas Recebidas',
        data: [45, 52, 48, 60, 55, 25, 15],
        color: '#f59e0b'
      }
    ]
  };

  // Mock data for technician performance
  const technicianPerformanceData = [
    {
      id: 1,
      name: 'Carlos Mendes',
      avatar: 'https://ui-avatars.com/api/?name=Carlos+Mendes&background=3b82f6&color=fff',
      completedTasks: 45,
      activeProjects: 3,
      averageRating: 4.9,
      efficiency: 95,
      responseTime: '1.2h'
    },
    {
      id: 2,
      name: 'Pedro Costa',
      avatar: 'https://ui-avatars.com/api/?name=Pedro+Costa&background=10b981&color=fff',
      completedTasks: 42,
      activeProjects: 4,
      averageRating: 4.7,
      efficiency: 88,
      responseTime: '2.1h'
    },
    {
      id: 3,
      name: 'Ana Paula',
      avatar: 'https://ui-avatars.com/api/?name=Ana+Paula&background=8b5cf6&color=fff',
      completedTasks: 48,
      activeProjects: 2,
      averageRating: 5.0,
      efficiency: 97,
      responseTime: '0.9h'
    },
    {
      id: 4,
      name: 'Ricardo Santos',
      avatar: 'https://ui-avatars.com/api/?name=Ricardo+Santos&background=f59e0b&color=fff',
      completedTasks: 38,
      activeProjects: 3,
      averageRating: 4.5,
      efficiency: 82,
      responseTime: '3.2h'
    }
  ];

  // System health data
  const systemHealthData = {
    cpuUsage: 45,
    memoryUsage: 62,
    diskUsage: 38,
    activeUsers: 127,
    requestsPerMinute: 850,
    averageResponseTime: 245,
    errorRate: 0.3,
    uptime: 99.8
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdate(new Date());
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setLastUpdate(new Date());
    setRefreshing(false);
  };

  const handleExportPDF = () => {
    console.log('Exporting to PDF...');
    // Implement PDF export logic
  };

  const handleExportExcel = () => {
    console.log('Exporting to Excel...');
    // Implement Excel export logic
  };

  const handleToggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header onToggleSidebar={handleToggleSidebar} />
      <main className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Page Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Performance Analytics
            </h1>
            <p className="text-muted-foreground">
              Monitoramento de KPIs e insights de desempenho operacional
            </p>
          </div>
          
          <div className="flex items-center space-x-4 mt-4 lg:mt-0">
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Icon name="Clock" size={16} />
              <span>Atualizado: {lastUpdate?.toLocaleTimeString('pt-BR')}</span>
            </div>
            <Button
              variant="outline"
              iconName="RefreshCw"
              iconPosition="left"
              loading={refreshing}
              onClick={handleRefresh}
            >
              Atualizar
            </Button>
            <div className="hidden md:flex space-x-2">
              <Button
                variant="outline"
                iconName="FileText"
                iconPosition="left"
                onClick={handleExportPDF}
              >
                Exportar PDF
              </Button>
              <Button
                variant="default"
                iconName="Download"
                iconPosition="left"
                onClick={handleExportExcel}
              >
                Exportar Excel
              </Button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <AnalyticsFilters
          dateRange={dateRange}
          department={department}
          onDateRangeChange={setDateRange}
          onDepartmentChange={setDepartment}
        />

        {/* KPI Widgets Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {kpiData?.map((kpi, index) => (
            <KPIWidget
              key={index}
              title={kpi?.title}
              value={kpi?.value}
              change={kpi?.change}
              changeType={kpi?.changeType}
              icon={kpi?.icon}
              description={kpi?.description}
              color={kpi?.color}
              loading={loading}
            />
          ))}
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <PerformanceChart
            title="Análise de Timeline de Projetos"
            subtitle="Projetos iniciados vs concluídos"
            data={projectTimelineData}
            type="line"
            loading={loading}
          />
          <PerformanceChart
            title="Utilização de Técnicos"
            subtitle="Taxa de utilização por técnico"
            data={technicianUtilizationData}
            type="bar"
            loading={loading}
          />
        </div>

        <div className="grid grid-cols-1 mb-8">
          <PerformanceChart
            title="Padrões de Demanda"
            subtitle="Distribuição de demandas por dia da semana"
            data={demandPatternsData}
            type="area"
            loading={loading}
          />
        </div>

        {/* Technician Performance Table */}
        <div className="mb-8">
          <TechnicianPerformanceTable
            technicians={technicianPerformanceData}
            loading={loading}
          />
        </div>

        {/* System Health Monitor */}
        <SystemHealthMonitor
          healthData={systemHealthData}
          loading={loading}
        />

        {/* Mobile Bottom Spacing */}
        <div className="h-20 lg:hidden"></div>
      </main>
    </div>
  );
};

export default PerformanceAnalytics;